import { useState, useEffect, useCallback } from 'react';
import { Trophy, RefreshCw, ExternalLink, Check, Clock, AlertCircle, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { LoadingPage } from '@/components/common/Loading';

const API_BASE = '';

const CATEGORY_LABELS = {
  european_club: { en: 'European Clubs', tr: 'Avrupa Kulupleri', color: 'blue' },
  international: { en: 'International', tr: 'Uluslararasi', color: 'amber' },
  national_cups: { en: 'National Cups', tr: 'Ulusal Kupalar', color: 'emerald' },
  super_cups: { en: 'Super Cups', tr: 'Super Kupalar', color: 'violet' },
};

export default function AdminCupsManager() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState({});
  const [filter, setFilter] = useState('all');

  const token = localStorage.getItem('admin_token');
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  const load = useCallback(() => {
    setLoading(true);
    fetch(`${API_BASE}/api/admin/cups`, { headers })
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const syncCup = async (slug) => {
    setSyncing(prev => ({ ...prev, [slug]: true }));
    try {
      const res = await fetch(`${API_BASE}/api/admin/cups/${slug}/sync`, { method: 'POST', headers });
      const result = await res.json();
      if (result.status === 'synced') {
        load();
      }
    } catch { /* ignore */ }
    setSyncing(prev => ({ ...prev, [slug]: false }));
  };

  const clearCache = async () => {
    await fetch(`${API_BASE}/api/admin/cups/cache`, { method: 'DELETE', headers });
    load();
  };

  if (loading) return <LoadingPage />;
  if (!data) return null;

  const { cups, stats } = data;
  const filtered = filter === 'all' ? cups : cups.filter(c => c.category === filter);

  return (
    <div data-testid="admin-cups">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight uppercase text-slate-50 flex items-center gap-2" style={{ fontFamily: 'var(--font-heading)' }}>
            <Trophy className="w-6 h-6 text-amber-400" />Cups & Tournaments
          </h1>
          <p className="text-sm text-slate-500 mt-1">{stats.total} cups | {stats.synced} synced | {stats.with_matches} with data</p>
        </div>
        <Button variant="outline" size="sm" onClick={clearCache} className="border-slate-700 text-slate-400 hover:text-white">
          <Trash2 className="w-4 h-4 mr-1" />Clear Cache
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-3 mb-6">
        {[
          { label: 'Total Cups', val: stats.total, color: 'amber' },
          { label: 'Synced', val: stats.synced, color: 'emerald' },
          { label: 'With Data', val: stats.with_matches, color: 'blue' },
          { label: 'Not Synced', val: stats.total - stats.synced, color: 'red' },
        ].map(s => (
          <div key={s.label} className="bg-slate-900/50 border border-slate-800 rounded-xl p-4 text-center">
            <p className={`text-2xl font-bold text-${s.color}-400`}>{s.val}</p>
            <p className="text-[10px] text-slate-500 uppercase">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Filter */}
      <div className="flex gap-2 mb-4 flex-wrap">
        <button onClick={() => setFilter('all')}
          className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${filter === 'all' ? 'bg-amber-500/20 text-amber-300 border border-amber-500/30' : 'bg-slate-800/40 text-slate-500 border border-transparent'}`}>
          All ({cups.length})
        </button>
        {Object.entries(CATEGORY_LABELS).map(([key, cat]) => {
          const count = cups.filter(c => c.category === key).length;
          return (
            <button key={key} onClick={() => setFilter(key)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${filter === key ? `bg-${cat.color}-500/20 text-${cat.color}-300 border border-${cat.color}-500/30` : 'bg-slate-800/40 text-slate-500 border border-transparent'}`}>
              {cat.en} ({count})
            </button>
          );
        })}
      </div>

      {/* Cups Table */}
      <div className="bg-slate-900/50 border border-slate-800 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs text-slate-500 border-b border-slate-800/50 bg-slate-900/80">
              <th className="text-left px-4 py-3">Cup</th>
              <th className="text-center px-3 py-3">API ID</th>
              <th className="text-center px-3 py-3">Season</th>
              <th className="text-center px-3 py-3">Category</th>
              <th className="text-center px-3 py-3">DB Matches</th>
              <th className="text-center px-3 py-3">Cached</th>
              <th className="text-center px-3 py-3">Last Sync</th>
              <th className="text-center px-3 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(cup => {
              const catLabel = CATEGORY_LABELS[cup.category];
              const isSyncing = syncing[cup.id];
              return (
                <tr key={cup.id} className="border-b border-slate-800/30 hover:bg-slate-800/20">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center shrink-0">
                        <img src={cup.logo_url} alt="" className="w-6 h-6 object-contain" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-slate-200">{cup.name}</p>
                        <p className="text-[10px] text-slate-500">{cup.country} - {cup.type}</p>
                      </div>
                    </div>
                  </td>
                  <td className="text-center px-3 py-3">
                    <code className="text-xs text-slate-400 bg-slate-800 px-1.5 py-0.5 rounded">{cup.api_id}</code>
                  </td>
                  <td className="text-center px-3 py-3 text-slate-400">{cup.season}</td>
                  <td className="text-center px-3 py-3">
                    <Badge className={`text-[10px] bg-${catLabel?.color || 'slate'}-500/10 text-${catLabel?.color || 'slate'}-400 border-${catLabel?.color || 'slate'}-500/20`}>
                      {catLabel?.en || cup.category}
                    </Badge>
                  </td>
                  <td className="text-center px-3 py-3">
                    <span className={cup.matches_in_db > 0 ? 'text-emerald-400 font-semibold' : 'text-slate-600'}>{cup.matches_in_db}</span>
                  </td>
                  <td className="text-center px-3 py-3">
                    <span className={cup.cached_fixtures > 0 ? 'text-blue-400' : 'text-slate-600'}>{cup.cached_fixtures}</span>
                  </td>
                  <td className="text-center px-3 py-3 text-[11px]">
                    {cup.last_sync ? (
                      <span className="text-emerald-400 flex items-center justify-center gap-1">
                        <Check className="w-3 h-3" />
                        {new Date(cup.last_sync).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </span>
                    ) : (
                      <span className="text-slate-600 flex items-center justify-center gap-1">
                        <Clock className="w-3 h-3" />Never
                      </span>
                    )}
                  </td>
                  <td className="text-center px-3 py-3">
                    <div className="flex items-center justify-center gap-1">
                      <Button variant="ghost" size="sm" onClick={() => syncCup(cup.id)} disabled={isSyncing}
                        className="text-amber-400 hover:text-amber-300 hover:bg-amber-500/10 h-7 px-2">
                        <RefreshCw className={`w-3.5 h-3.5 ${isSyncing ? 'animate-spin' : ''}`} />
                      </Button>
                      <a href={`/tr/kupa/${cup.id}`} target="_blank" rel="noopener noreferrer">
                        <Button variant="ghost" size="sm" className="text-slate-500 hover:text-slate-300 h-7 px-2">
                          <ExternalLink className="w-3.5 h-3.5" />
                        </Button>
                      </a>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
